import { Alert, ScrollArea, Skeleton, TreeList } from '@ibsys/kit';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useKmDomains, useKmEntityTypes } from '@/entities/ontology';
import { useMainLayout } from '@/shared/ui';
import { EntityTypeListItem } from './EntityTypeListItem';
import { groupEntityTypesByDomain } from './groupEntityTypesByDomain';

type Props = {
  search: string;
};

/**
 * Левая панель: типы сущностей, сгруппированные по доменам, с клиентской
 * фильтрацией по названию и коду.
 */
export const OntologyList: FC<Props> = ({ search }) => {
  const { t } = useTranslation('ontology');
  const { code } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const { isMobile } = useMainLayout();

  const { data, isLoading } = useKmEntityTypes({ includeDeleted: false, limit: 1000 });
  const { data: domainsData } = useKmDomains({ includeDeleted: false, limit: 1000 });

  const items = useMemo(() => data?.items ?? [], [data]);
  const domains = useMemo(() => domainsData?.items ?? [], [domainsData]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter(
      (item) => item.name.toLowerCase().includes(query) || item.code.toLowerCase().includes(query),
    );
  }, [items, search]);

  // Пока ни один тип не отнесён к домену, группировать нечего — список остаётся плоским
  const hasDomains = useMemo(() => items.some((item) => item.domainCode), [items]);

  const groups = useMemo(
    () => (hasDomains ? groupEntityTypesByDomain(filtered, domains, t('withoutDomain')) : []),
    [hasDomains, filtered, domains, t],
  );

  // Хранятся свёрнутые группы, а не раскрытые: по умолчанию раскрыты все, включая появившиеся позже
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);

  const expandedGroups = useMemo(() => {
    const keys = groups.map((group) => group.key);
    // Поиск раскрывает группы: иначе найденный тип остался бы внутри свёрнутой
    return search.trim() ? keys : keys.filter((key) => !collapsedGroups.includes(key));
  }, [groups, collapsedGroups, search]);

  const handleExpandedChange = (value: string[]) => {
    setCollapsedGroups(groups.map((group) => group.key).filter((key) => !value.includes(key)));
  };

  const firstTypeCode = hasDomains ? groups[0]?.entityTypes[0]?.code : filtered[0]?.code;

  // Автонавигация на первый тип на широких экранах, пока тип не выбран
  useEffect(() => {
    if (!code && !isMobile && firstTypeCode && !isLoading) {
      navigate(`/ontology/${firstTypeCode}`, { replace: true });
    }
  }, [code, isMobile, firstTypeCode, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 h-full">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  if (!filtered.length) {
    return <Alert type="info" className="text-muted-foreground p-4" title={t('listIsEmpty')} />;
  }

  if (!hasDomains) {
    return (
      <ScrollArea>
        <ul className="flex flex-col gap-4">
          {filtered.map((item) => (
            <EntityTypeListItem key={item.code} entityType={item} isActive={item.code === code} />
          ))}
        </ul>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea>
      <TreeList.Root value={expandedGroups} onValueChange={handleExpandedChange}>
        {groups.map((group) => (
          <TreeList.Group
            key={group.key}
            value={group.key}
            classNames={{ content: 'p-3' }}
            label={
              <div className="flex flex-1 items-center gap-2">
                <span className="line-clamp-2 text-left text-sm font-medium leading-tight">{group.label}</span>
                <span className="text-xs text-muted-foreground">{`(${group.entityTypes.length})`}</span>
              </div>
            }
          >
            <ul className="flex flex-col gap-4">
              {group.entityTypes.map((item) => (
                <EntityTypeListItem key={item.code} entityType={item} isActive={item.code === code} />
              ))}
            </ul>
          </TreeList.Group>
        ))}
      </TreeList.Root>
    </ScrollArea>
  );
};
